import { type ReactElement, useState } from 'react';
import { Button } from '@/shared/ui';
import { type RevealedQuotePhone, useRevealQuotePhone } from '../model/use-reveal-quote-phone';

export interface RevealPhoneButtonProps {
  readonly quoteRequestId: string;
}

const AUDIT_NOTICE = '보기를 누르면 접속 기록이 남습니다.';
const NO_PHONE_NOTICE = '본인인증 전이거나 탈퇴해 휴대폰 번호가 없습니다.';

/**
 * 휴대폰 보기(견적 설계 §6-2) — 복호화 값은 이 컴포넌트 state 에만 두고 목록·상세 캐시에 쓰지 않는다.
 * 숨기기를 누르면 state 에서 지우고, 다시 보려면 서버에 다시 묻는다(감사 로그가 또 남는다).
 */
export const RevealPhoneButton = ({ quoteRequestId }: RevealPhoneButtonProps): ReactElement => {
  const [revealed, setRevealed] = useState<RevealedQuotePhone | null>(null);
  const { reveal, isPending, error, reset } = useRevealQuotePhone();

  const handleReveal = async (): Promise<void> => {
    const result = await reveal(quoteRequestId);

    if (result !== undefined) {
      setRevealed(result);
    }
  };

  if (revealed !== null) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {revealed.phone === null ? (
          <span className="text-muted-foreground">{NO_PHONE_NOTICE}</span>
        ) : (
          <span className="tabular-nums">{revealed.phone}</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setRevealed(null);
            reset();
          }}
        >
          숨기기
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          isLoading={isPending}
          onClick={() => {
            void handleReveal();
          }}
        >
          휴대폰 보기
        </Button>
        <span className="text-xs text-muted-foreground">{AUDIT_NOTICE}</span>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};
